import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import Logo from "@/components/Logo";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";

export default function Register() {
    const [name, setName] = useState("");
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [busy, setBusy] = useState(false);
    const { register } = useAuth();
    const navigate = useNavigate();

    async function submit(e) {
        e.preventDefault();
        setBusy(true);
        try {
            await register(email.trim().toLowerCase(), password, name.trim());
            navigate("/onboarding", { replace: true });
            toast.success("Welcome to SRT Finder");
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setBusy(false);
        }
    }

    function google() {
        // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
        const redirectUrl = window.location.origin + "/onboarding";
        window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
    }

    return (
        <div className="min-h-screen bg-onyx-900 flex items-center justify-center px-4 py-10">
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="w-full max-w-md surface rounded-md p-8"
            >
                <div className="flex justify-center mb-6">
                    <Logo size="lg" />
                </div>
                <h1 className="headline text-3xl text-center">Join the pack</h1>
                <p className="text-center text-sm text-onyx-500 mt-1">
                    SRT / R/T & above only. No V6 tire kickers.
                </p>

                <form onSubmit={submit} className="mt-6 space-y-4">
                    <div>
                        <label className="label-caps block mb-1.5">Name</label>
                        <input
                            data-testid="register-name"
                            required
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            className="w-full bg-onyx-700 border border-onyx-600 rounded-sm px-3 py-3 text-white focus:outline-none focus:border-hellcat"
                            placeholder="Marcus Reyes"
                        />
                    </div>
                    <div>
                        <label className="label-caps block mb-1.5">Email</label>
                        <input
                            data-testid="register-email"
                            type="email"
                            required
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            className="w-full bg-onyx-700 border border-onyx-600 rounded-sm px-3 py-3 text-white focus:outline-none focus:border-hellcat"
                            placeholder="you@example.com"
                        />
                    </div>
                    <div>
                        <label className="label-caps block mb-1.5">Password</label>
                        <input
                            data-testid="register-password"
                            type="password"
                            required
                            minLength={6}
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            className="w-full bg-onyx-700 border border-onyx-600 rounded-sm px-3 py-3 text-white focus:outline-none focus:border-hellcat"
                            placeholder="At least 6 characters"
                        />
                    </div>
                    <button
                        data-testid="register-submit"
                        disabled={busy}
                        className="btn-red w-full py-3 rounded-sm disabled:opacity-60"
                    >
                        {busy ? "Creating..." : "Create account"}
                    </button>
                </form>

                <div className="flex items-center gap-3 my-6">
                    <div className="flex-1 divider" />
                    <span className="label-caps text-[10px]">or</span>
                    <div className="flex-1 divider" />
                </div>

                <button
                    data-testid="register-google"
                    onClick={google}
                    className="btn-ghost w-full py-3 rounded-sm flex items-center justify-center gap-3 font-bold uppercase tracking-widest text-sm"
                >
                    Continue with Google
                </button>

                <p className="text-center text-sm text-onyx-500 mt-6">
                    Already have an account?{" "}
                    <Link to="/login" className="text-hellcat font-bold">
                        Sign in
                    </Link>
                </p>
            </motion.div>
        </div>
    );
}
